import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { toast } from 'sonner';
import { api } from '@/api';
import {
  CheckSquare, Plus, Search, Filter, Calendar, Clock, User, Users,
  FileText, ChevronRight, AlertCircle, CheckCircle2, Timer,
  Loader2, ArrowRight, ExternalLink, MessageSquare, Briefcase,
  ListFilter, LayoutGrid, Eye, MoreHorizontal, Trash2, Edit,
  CalendarClock, Send, UserCheck, Building2
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  { value: 'awaiting_response', label: 'Awaiting Response', color: 'bg-purple-100 text-purple-700' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700' },
  { value: 'closed', label: 'Closed', color: 'bg-slate-100 text-slate-700' },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'bg-slate-100 text-slate-600' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-amber-100 text-amber-700' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' },
];

const SOURCE_BADGE = {
  meeting_minutes: { label: 'Meeting Minute', className: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  user_created: { label: 'User Created', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const statusColor = (s) => STATUS_OPTIONS.find(o => o.value === s)?.color || 'bg-slate-100 text-slate-700';
const priorityColor = (p) => PRIORITY_OPTIONS.find(o => o.value === p)?.color || 'bg-slate-100 text-slate-600';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};

const daysUntil = (d) => {
  if (!d) return null;
  try { return Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24)); }
  catch { return null; }
};

// Sidebar view definitions
const SIDEBAR_VIEWS = [
  { id: 'all', label: 'All Tasks', icon: CheckSquare },
  { id: 'my', label: 'My Tasks', icon: User },
  { id: 'meeting_minutes', label: 'Meeting Minute Tasks', icon: FileText },
  { id: 'user_created', label: 'User Created Tasks', icon: Briefcase },
  { id: 'overdue', label: 'Overdue', icon: AlertCircle },
  { id: 'completed', label: 'Completed', icon: CheckCircle2 },
];

export default function TasksPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [sidebarView, setSidebarView] = useState('all');
  const [groupBy, setGroupBy] = useState('none');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newTask, setNewTask] = useState({
    title: '', description: '', priority: 'medium', due_date: '',
    assigned_to_name: '', assignee_type: 'internal_user', source_type: 'user_created'
  });

  // Load projects
  useEffect(() => {
    (async () => {
      try {
        const res = await api.projects.getAll();
        const list = Array.isArray(res.data) ? res.data : res.data?.projects || [];
        setProjects(list);
        if (list.length > 0 && !selectedProject) setSelectedProject(list[0].id);
      } catch (e) { console.error('Failed to load projects', e); }
    })();
  }, []);

  // Load tasks
  const loadTasks = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const params = {};
      if (sidebarView === 'meeting_minutes') params.source_type = 'meeting_minutes';
      if (sidebarView === 'user_created') params.source_type = 'user_created';
      if (sidebarView === 'completed') params.status = 'completed';
      if (sidebarView === 'meeting_minutes' && groupBy === 'none') params.group_by = 'meeting';

      const res = await api.tasks.getByProject(selectedProject, params);
      const data = res.data;
      setTasks(data.tasks || []);
      setStats(data.stats || {});
    } catch (e) {
      console.error('Failed to load tasks', e);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [selectedProject, sidebarView, groupBy]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  // Auto-set groupBy when switching views
  useEffect(() => {
    if (sidebarView === 'meeting_minutes') setGroupBy('meeting');
    else if (sidebarView === 'user_created') setGroupBy('none');
    else if (sidebarView === 'all') setGroupBy('source');
  }, [sidebarView]);

  // Filter tasks client-side
  const filteredTasks = tasks.filter(t => {
    if (sidebarView === 'my') {
      // Simple heuristic — match current user
    }
    if (sidebarView === 'overdue') {
      const days = daysUntil(t.due_date);
      if (days === null || days >= 0) return false;
      if (['completed', 'closed', 'cancelled'].includes(t.status)) return false;
    }
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    if (filterPriority !== 'all' && t.priority !== filterPriority) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const match = (t.title || '').toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q) ||
        (t.assigned_to_name || '').toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  });

  // Group tasks
  const groupedTasks = () => {
    if (groupBy === 'none') return [{ key: 'all', label: 'All Tasks', tasks: filteredTasks }];

    const groups = {};
    filteredTasks.forEach(t => {
      let key, label;
      switch (groupBy) {
        case 'meeting':
          key = t.meeting_context?.meeting_id || '__no_meeting__';
          label = t.meeting_context?.meeting_title
            ? `${t.meeting_context.meeting_title}${t.meeting_context.meeting_date ? ` — ${fmtDate(t.meeting_context.meeting_date)}` : ''}`
            : 'User Created Tasks';
          break;
        case 'status':
          key = t.status || 'open';
          label = STATUS_OPTIONS.find(o => o.value === key)?.label || key;
          break;
        case 'assignee':
          key = t.assigned_to_name || '__unassigned__';
          label = t.assigned_to_name || 'Unassigned';
          break;
        case 'source':
          key = t.source_type || 'user_created';
          label = SOURCE_BADGE[key]?.label || key;
          break;
        case 'due_date':
          const days = daysUntil(t.due_date);
          if (days === null) { key = 'no_date'; label = 'No Due Date'; }
          else if (days < 0) { key = 'overdue'; label = 'Overdue'; }
          else if (days <= 7) { key = 'this_week'; label = 'Due This Week'; }
          else { key = 'later'; label = 'Due Later'; }
          break;
        default:
          key = 'all'; label = 'All Tasks';
      }
      if (!groups[key]) groups[key] = { key, label, tasks: [] };
      groups[key].tasks.push(t);
    });
    return Object.values(groups);
  };

  // Create task
  const handleCreate = async () => {
    if (!newTask.title.trim()) return toast.error('Task title is required');
    try {
      await api.tasks.create(selectedProject, {
        ...newTask,
        source_type: 'user_created',
      });
      toast.success('Task created');
      setShowCreateDialog(false);
      setNewTask({ title: '', description: '', priority: 'medium', due_date: '', assigned_to_name: '', assignee_type: 'internal_user', source_type: 'user_created' });
      loadTasks();
    } catch (e) {
      toast.error('Failed to create task');
    }
  };

  // Update task status
  const handleStatusChange = async (task, newStatus) => {
    try {
      await api.tasks.update(selectedProject, task.id, { status: newStatus });
      toast.success(`Task marked as ${newStatus.replace('_', ' ')}`);
      loadTasks();
      if (selectedTask?.id === task.id) {
        setSelectedTask({ ...selectedTask, status: newStatus });
      }
    } catch (e) {
      toast.error('Failed to update task');
    }
  };

  // Delete task
  const handleDelete = async (taskId) => {
    try {
      await api.tasks.delete(selectedProject, taskId);
      toast.success('Task deleted');
      setSelectedTask(null);
      loadTasks();
    } catch (e) {
      toast.error('Failed to delete task');
    }
  };

  const projectName = projects.find(p => p.id === selectedProject)?.name || '';

  return (
    <DashboardLayout>
      <div className="flex h-[calc(100vh-64px)]" data-testid="tasks-page">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-slate-200 flex-shrink-0 overflow-y-auto hidden lg:block" data-testid="tasks-sidebar">
          <div className="p-4">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Views</h2>
            {SIDEBAR_VIEWS.map(view => {
              const Icon = view.icon;
              const count = view.id === 'all' ? stats.total :
                view.id === 'my' ? null :
                view.id === 'meeting_minutes' ? stats.meeting_minutes :
                view.id === 'user_created' ? stats.user_created :
                view.id === 'overdue' ? stats.overdue :
                view.id === 'completed' ? stats.completed : null;
              return (
                <button
                  key={view.id}
                  onClick={() => setSidebarView(view.id)}
                  data-testid={`sidebar-view-${view.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors mb-1 ${
                    sidebarView === view.id
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="flex-1 text-left">{view.label}</span>
                  {count !== null && count !== undefined && (
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                      sidebarView === view.id ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'
                    }`}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Project selector */}
          <div className="p-4 border-t border-slate-100">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Project</h3>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="rounded-sm text-sm" data-testid="project-selector">
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto bg-slate-50">
          <div className="p-4 md:p-6 max-w-[1200px] mx-auto space-y-4">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className="text-xl font-bold text-slate-900">Tasks</h1>
                <p className="text-sm text-slate-500">{projectName}</p>
              </div>
              <div className="flex gap-2">
                {/* Mobile project selector */}
                <div className="lg:hidden">
                  <Select value={selectedProject} onValueChange={setSelectedProject}>
                    <SelectTrigger className="rounded-sm text-sm w-48">
                      <SelectValue placeholder="Project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={() => setShowCreateDialog(true)} className="rounded-sm bg-slate-900" data-testid="create-task-btn">
                  <Plus className="h-4 w-4 mr-2" /> New Task
                </Button>
              </div>
            </div>

            {/* Source tabs (mobile-friendly sidebar replacement) */}
            <div className="flex gap-2 overflow-x-auto pb-1 lg:hidden">
              {SIDEBAR_VIEWS.map(view => (
                <Button key={view.id} variant={sidebarView === view.id ? 'default' : 'outline'} size="sm"
                  onClick={() => setSidebarView(view.id)}
                  className={`rounded-sm whitespace-nowrap ${sidebarView === view.id ? 'bg-slate-900' : ''}`}
                >
                  {view.label}
                </Button>
              ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <Card className="rounded-sm"><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-slate-900">{stats.total || 0}</p>
                <p className="text-xs text-slate-500 uppercase">Total</p>
              </CardContent></Card>
              <Card className="rounded-sm"><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.open || 0}</p>
                <p className="text-xs text-slate-500 uppercase">Open</p>
              </CardContent></Card>
              <Card className="rounded-sm"><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-amber-600">{stats.in_progress || 0}</p>
                <p className="text-xs text-slate-500 uppercase">In Progress</p>
              </CardContent></Card>
              <Card className="rounded-sm"><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-red-600">{stats.overdue || 0}</p>
                <p className="text-xs text-slate-500 uppercase">Overdue</p>
              </CardContent></Card>
              <Card className="rounded-sm"><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-indigo-600">{stats.meeting_minutes || 0}</p>
                <p className="text-xs text-slate-500 uppercase">From Minutes</p>
              </CardContent></Card>
              <Card className="rounded-sm"><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.completed || 0}</p>
                <p className="text-xs text-slate-500 uppercase">Completed</p>
              </CardContent></Card>
            </div>

            {/* Filters row */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Search tasks..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  className="pl-9 rounded-sm" data-testid="task-search" />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40 rounded-sm" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger className="w-36 rounded-sm" data-testid="filter-priority">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  {PRIORITY_OPTIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger className="w-40 rounded-sm" data-testid="group-by">
                  <SelectValue placeholder="Group by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Grouping</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                  <SelectItem value="assignee">Assignee</SelectItem>
                  <SelectItem value="source">Source</SelectItem>
                  <SelectItem value="due_date">Due Date</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Task List */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : filteredTasks.length === 0 ? (
              <Card className="rounded-sm">
                <CardContent className="p-12 text-center">
                  <CheckSquare className="h-16 w-16 text-slate-200 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900">No tasks found</h3>
                  <p className="text-slate-500 mb-4">
                    {sidebarView === 'meeting_minutes' ? 'No meeting minute tasks yet. Extract tasks from meeting minutes to see them here.' :
                     sidebarView === 'overdue' ? 'No overdue tasks. Great job!' :
                     'Create your first task to get started.'}
                  </p>
                  <Button onClick={() => setShowCreateDialog(true)} className="rounded-sm bg-slate-900">
                    <Plus className="h-4 w-4 mr-2" /> Create Task
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {groupedTasks().map(group => (
                  <div key={group.key}>
                    {groupBy !== 'none' && (
                      <div className="flex items-center gap-2 mb-2 mt-4" data-testid={`group-header-${group.key}`}>
                        {groupBy === 'meeting' && group.key !== '__no_meeting__' && (
                          <FileText className="h-4 w-4 text-indigo-500" />
                        )}
                        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">{group.label}</h3>
                        <Badge className="bg-slate-200 text-slate-700">{group.tasks.length}</Badge>
                      </div>
                    )}
                    <Card className="rounded-sm border-slate-200">
                      <CardContent className="p-0 divide-y divide-slate-100">
                        {group.tasks.map(task => (
                          <TaskRow key={task.id} task={task} onClick={() => setSelectedTask(task)}
                            onStatusChange={handleStatusChange} />
                        ))}
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Task Detail Drawer */}
        {selectedTask && (
          <TaskDetailDrawer
            task={selectedTask}
            onClose={() => setSelectedTask(null)}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
            navigate={navigate}
          />
        )}
      </div>

      {/* Create Task Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Title *</label>
              <Input value={newTask.title} onChange={e => setNewTask({ ...newTask, title: e.target.value })}
                placeholder="Task title" className="rounded-sm" data-testid="new-task-title" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Description</label>
              <Textarea value={newTask.description} onChange={e => setNewTask({ ...newTask, description: e.target.value })}
                placeholder="Task description" className="rounded-sm" rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Priority</label>
                <Select value={newTask.priority} onValueChange={v => setNewTask({ ...newTask, priority: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Due Date</label>
                <Input type="date" value={newTask.due_date} onChange={e => setNewTask({ ...newTask, due_date: e.target.value })}
                  className="rounded-sm" data-testid="new-task-due-date" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Assign To</label>
              <Input value={newTask.assigned_to_name} onChange={e => setNewTask({ ...newTask, assigned_to_name: e.target.value })}
                placeholder="Person or role name" className="rounded-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Assignee Type</label>
              <Select value={newTask.assignee_type} onValueChange={v => setNewTask({ ...newTask, assignee_type: v })}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal_user">Internal User</SelectItem>
                  <SelectItem value="consultant">Consultant</SelectItem>
                  <SelectItem value="subcontractor">Subcontractor</SelectItem>
                  <SelectItem value="external_contact">External Contact</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleCreate} className="rounded-sm bg-slate-900" data-testid="create-task-submit">Create Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

// Individual task row component
function TaskRow({ task, onClick, onStatusChange }) {
  const days = daysUntil(task.due_date);
  const isOverdue = days !== null && days < 0 && !['completed', 'closed', 'cancelled'].includes(task.status);
  const source = SOURCE_BADGE[task.source_type] || SOURCE_BADGE.user_created;

  return (
    <div className={`flex items-center gap-4 p-4 hover:bg-slate-50 cursor-pointer transition-colors ${
      isOverdue ? 'bg-red-50/40' : ''
    }`} onClick={onClick} data-testid={`task-row-${task.id}`}>
      {/* Status checkbox */}
      <button
        onClick={e => { e.stopPropagation(); onStatusChange(task, task.status === 'completed' ? 'open' : 'completed'); }}
        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
          task.status === 'completed' ? 'bg-green-500 border-green-500 text-white' : 'border-slate-300 hover:border-slate-500'
        }`}
        data-testid="task-toggle-complete"
      >
        {task.status === 'completed' && <CheckCircle2 className="h-3 w-3" />}
      </button>

      {/* Task info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className={`font-medium text-sm ${task.status === 'completed' ? 'line-through text-slate-400' : 'text-slate-900'}`}>
            {task.title}
          </span>
          <Badge className={`text-[10px] px-1.5 py-0 border ${source.className}`} data-testid="task-source-badge">
            {source.label}
          </Badge>
          {task.meeting_context?.action_number && (
            <Badge className="text-[10px] px-1.5 py-0 bg-slate-100 text-slate-500 border border-slate-200">
              {task.meeting_context.action_number}
            </Badge>
          )}
        </div>
        {task.description && (
          <p className="text-xs text-slate-500 truncate max-w-md">{task.description}</p>
        )}
        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400 flex-wrap">
          {task.assigned_to_name && (
            <span className="flex items-center gap-1">
              <UserCheck className="h-3 w-3" /> {task.assigned_to_name}
            </span>
          )}
          {task.due_date && (
            <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-semibold' : ''}`}>
              <Calendar className="h-3 w-3" /> {fmtDate(task.due_date)}
            </span>
          )}
          {isOverdue && (
            <span className="text-red-600 font-bold flex items-center gap-1">
              <Timer className="h-3 w-3" /> {Math.abs(days)}d overdue
            </span>
          )}
          {days !== null && days >= 0 && days <= 7 && task.status !== 'completed' && (
            <span className="text-amber-600 font-semibold flex items-center gap-1">
              <Timer className="h-3 w-3" /> {days}d left
            </span>
          )}
        </div>
      </div>

      {/* Right side badges */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <Badge className={`text-[10px] rounded-sm ${statusColor(task.status)}`}>{task.status?.replace('_', ' ')}</Badge>
        <Badge className={`text-[10px] rounded-sm ${priorityColor(task.priority)}`}>{task.priority}</Badge>
        <ChevronRight className="h-4 w-4 text-slate-300" />
      </div>
    </div>
  );
}

// Task detail drawer
function TaskDetailDrawer({ task, onClose, onStatusChange, onDelete, navigate }) {
  const source = SOURCE_BADGE[task.source_type] || SOURCE_BADGE.user_created;
  const days = daysUntil(task.due_date);
  const isOverdue = days !== null && days < 0 && !['completed', 'closed'].includes(task.status);

  return (
    <div className="w-96 bg-white border-l border-slate-200 overflow-y-auto flex-shrink-0 hidden xl:block" data-testid="task-detail-drawer">
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <Badge className={`border ${source.className}`} data-testid="detail-source-badge">{source.label}</Badge>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => onDelete(task.id)} data-testid="delete-task-btn">
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <h2 className="text-lg font-bold text-slate-900 mb-1">{task.title}</h2>
        {task.description && <p className="text-sm text-slate-500">{task.description}</p>}
      </div>

      <div className="p-4 space-y-4">
        {/* Status */}
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Status</label>
          <Select value={task.status} onValueChange={v => onStatusChange(task, v)}>
            <SelectTrigger className="rounded-sm" data-testid="detail-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Priority */}
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Priority</label>
          <Badge className={`${priorityColor(task.priority)}`}>{task.priority}</Badge>
        </div>

        {/* Assignment */}
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Assigned To</label>
          <div className="flex items-center gap-2 text-sm">
            <UserCheck className="h-4 w-4 text-slate-400" />
            <span className="text-slate-700">{task.assigned_to_name || 'Unassigned'}</span>
          </div>
          {task.assignee_type && (
            <p className="text-xs text-slate-400 mt-1 capitalize">{task.assignee_type.replace('_', ' ')}</p>
          )}
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Due Date</label>
            <p className={`text-sm ${isOverdue ? 'text-red-600 font-bold' : 'text-slate-700'}`}>
              {fmtDate(task.due_date)}
            </p>
            {isOverdue && <p className="text-xs text-red-500">{Math.abs(days)}d overdue</p>}
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Created</label>
            <p className="text-sm text-slate-700">{fmtDate(task.created_at)}</p>
          </div>
        </div>

        {/* Assigned by */}
        {task.assigned_by_name && (
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Assigned By</label>
            <p className="text-sm text-slate-700">{task.assigned_by_name}</p>
          </div>
        )}

        {/* Meeting Context (for meeting minute tasks) */}
        {task.source_type === 'meeting_minutes' && task.meeting_context && (
          <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
            <h4 className="text-xs font-bold text-indigo-800 uppercase mb-2">Meeting Source</h4>
            <p className="text-sm font-medium text-indigo-900 mb-1">{task.meeting_context.meeting_title || 'Meeting'}</p>
            {task.meeting_context.meeting_date && (
              <p className="text-xs text-indigo-600 mb-2">{fmtDate(task.meeting_context.meeting_date)}</p>
            )}
            {task.meeting_context.action_number && (
              <p className="text-xs text-indigo-600 mb-2">Action: {task.meeting_context.action_number}</p>
            )}
            {task.meeting_context.source_paragraph && (
              <div className="mt-2 bg-white/70 rounded p-2 border border-indigo-100">
                <p className="text-xs font-bold text-indigo-700 mb-1">Source Paragraph</p>
                <p className="text-xs text-slate-600 italic">"{task.meeting_context.source_paragraph}"</p>
              </div>
            )}
            {task.meeting_context.meeting_id && (
              <Button variant="outline" size="sm" className="mt-2 w-full rounded-sm border-indigo-200 text-indigo-700"
                onClick={() => navigate(`/meeting-minutes/${task.meeting_context.meeting_id}`)}
                data-testid="view-meeting-btn">
                <ExternalLink className="h-3 w-3 mr-2" /> View Meeting Minutes
              </Button>
            )}
          </div>
        )}

        {/* Notes */}
        {task.notes && task.notes.length > 0 && (
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Notes</label>
            <div className="space-y-2">
              {task.notes.map(note => (
                <div key={note.id} className="bg-slate-50 rounded p-2 text-sm">
                  <p className="text-slate-700">{note.text}</p>
                  <p className="text-xs text-slate-400 mt-1">{note.created_by_name} — {fmtDate(note.created_at)}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
